/* import __COLOCATED_TEMPLATE__ from './selected-log-container.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';

export default class SelectedLogContainer extends Component {
  @tracked openSectionId;
  @service intl;

  @action
  onOpenSectionChange(sectionId) {
    this.openSectionId = sectionId;
  }

  get isClientSide() {
    return this.args.selectedAction?.isClientSide;
  }

  get clientFunctionName() {
    return this.args.selectedAction?.clientFunctionName;
  }

  get executionTime() {
    return this.args.selectedAction.executionTimeMs
      ? this.intl.t('components.workflow-connector.builder.selected-log.execution-time', {
          executionTime: this.args.selectedAction.executionTimeMs,
        })
      : null;
  }

  get requestBannerErrorText() {
    return this.intl.t('components.workflow-connector.builder.selected-log.banner-text-help');
  }

  get requestBannerText() {
    return this.requestSuccessful
      ? this.intl.t(
          'components.workflow-connector.builder.selected-log.request-banner-text-success',
        )
      : this.args.selectedAction.errorType;
  }

  get requestBannerType() {
    return this.requestSuccessful ? 'green' : 'red';
  }

  get responseBannerSuccessText() {
    // Client-side actions don't have HTTP status, use a different message
    if (this.isClientSide) {
      return this.intl.t(
        'components.workflow-connector.builder.selected-log.client-side.response-banner-text-success',
      );
    }
    return this.intl.t(
      'components.workflow-connector.builder.selected-log.response-banner-text-success',
      {
        httpStatus: this.args.selectedAction.httpStatus,
      },
    );
  }

  get responseBannerText() {
    if (!this.requestSuccessful) {
      return this.requestBannerErrorText;
    }
    if (!this.responseSuccessful) {
      return this.args.selectedAction.errorType;
    }
    return this.bodyIsEmpty(this.args.selectedAction.responseBody)
      ? this.intl.t(
          'components.workflow-connector.builder.selected-log.fin-usage.response-banner-text-no-data',
        )
      : this.responseBannerSuccessText;
  }

  get responseFullErrorMessage() {
    return this.intl.t(
      'components.workflow-connector.builder.selected-log.response-banner-text-full-error',
      {
        errorMessage: this.errorMessage,
      },
    );
  }

  get responseBannerType() {
    if (!this.requestSuccessful) {
      return 'gray';
    }
    if (!this.responseSuccessful) {
      return 'red';
    }
    return this.bodyIsEmpty(this.args.selectedAction.responseBody) ? 'gray' : 'green';
  }

  get responseMappingBannerText() {
    if (this.requestSuccessful && this.responseSuccessful) {
      return this.responseMappingSuccessful
        ? this.intl.t(
            'components.workflow-connector.builder.selected-log.mapping-banner-text-success',
          )
        : this.args.selectedAction.errorType;
    }
    return this.intl.t('components.workflow-connector.builder.selected-log.banner-text-help');
  }

  get responseMappingBannerType() {
    if (this.requestSuccessful && this.responseSuccessful) {
      return this.responseMappingSuccessful ? 'green' : 'red';
    }
    return 'gray';
  }

  get mappingHelpMessage() {
    return this.intl.t('components.workflow-connector.builder.selected-log.mapping-help-message');
  }

  get requestBody() {
    if (this.args.selectedAction.requestBody) {
      try {
        let json = JSON.parse(this.args.selectedAction.requestBody);
        return JSON.stringify(json, null, 2);
      } catch (error) {
        // If it's not valid JSON, return the raw string
        return this.args.selectedAction.requestBody;
      }
    }
    return null;
  }

  get responseBody() {
    let body = this.args.selectedAction.responseBody;
    if (!this.bodyIsEmpty(body) && this.responseSuccessful) {
      try {
        let json = JSON.parse(body);
        return JSON.stringify(json, null, 2);
      } catch (error) {
        // If it's not valid JSON, return the raw value as a string
        return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
      }
    }
    return null;
  }

  get rawResponseBody() {
    if (this.args.selectedAction.rawResponseBody) {
      try {
        let json = JSON.parse(this.args.selectedAction.rawResponseBody);
        return JSON.stringify(json, null, 2);
      } catch (error) {
        // If it's not valid JSON, return the raw string
        return this.args.selectedAction.rawResponseBody;
      }
    }
    return null;
  }

  get requestSuccessful() {
    // For client-side actions, the request is successful if we have a function name
    if (this.isClientSide) {
      return !!this.clientFunctionName;
    }
    return this.args.selectedAction.httpStatus > 0;
  }

  get hasRequestData() {
    // Returns true when request data (URL, method, headers) is available to display,
    // even if the request resulted in a timeout or other network-level error (httpStatus === 0).
    // This is distinct from requestSuccessful which checks if the HTTP response was received.
    if (this.isClientSide) {
      return !!this.clientFunctionName;
    }
    return !!this.args.selectedAction.requestUrl || !!this.args.selectedAction.httpMethod;
  }

  get responseSuccessful() {
    // For client-side actions, use the success field directly
    if (this.isClientSide) {
      return this.args.selectedAction.success;
    }
    return this.args.selectedAction.httpStatus >= 200 && this.args.selectedAction.httpStatus < 300;
  }

  get responseMappingSuccessful() {
    return this.args.selectedAction.errorMessage === null;
  }

  get errorMessage() {
    return this.args.selectedAction.errorMessage;
  }

  get httpMethodString() {
    return this.args.selectedAction.httpMethod
      ? this.args.selectedAction.httpMethod.toUpperCase()
      : '';
  }

  get isCustomBot() {
    let sourceType = this.args.selectedAction.sourceType;
    let botText = this.intl.t('components.workflow-connector.builder.selected-log.bot');
    if (sourceType && sourceType.includes(botText)) {
      return true;
    }
    return false;
  }

  bodyIsEmpty(body) {
    if (body === null || body === undefined || body === '') {
      return true;
    }

    try {
      let jsonBody = typeof body === 'string' ? JSON.parse(body) : body;
      return Array.isArray(jsonBody) ? jsonBody.length === 0 : Object.keys(jsonBody).length === 0;
    } catch (error) {
      // If parsing fails, consider the body non-empty (it has some content)
      return false;
    }
  }
}
